import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { imagePickSize } from 'utils/general';
import { fetchOrgMembers } from 'apollo/organization.query';
// import PropTypes from 'prop-types';
import clsx from 'clsx';

import Avatar from 'components/Avatar';
import DropdownButton from 'components/Button/DropdownButton/DropdownButton';
import APResponsiveDropdown from 'components/Dropdown/APResponsiveDropdown';
import ErrorModal from 'components/Modal/ConfirmationModal';
import { CREATE_LEAD_ASSIGNMENT, DELETE_LEAD_ASSIGNMENT } from 'containers/Leads/types';
import './styles.scss';

/**
 * @description: Leads Assignment Dropdown Component from leads index.
 * @param {*}
 * @return {*}
 */
export const AssignmentDropdown = ({ leadId, assignments }) => {
  const dispatch = useDispatch();
  const organization = useSelector((state) => state.global.userInfo.organization);
  const { id: orgId } = organization;
  const emptyAssignment = [{ name: 'TBD' }];

  const [orgMembers, setOrgMembers] = useState([]);
  const [assignmentState, setAssignmentState] = useState(emptyAssignment);
  const [showOrgMembers, toggleMemberDropdown] = useState(false);
  const [assignmentError, setAssignmentError] = useState(false);

  const assignees = useMemo(() => {
    return orgMembers.map((member) => {
      const { permissions } = member;
      // Org member has access to all leads if lead.all is true.
      // Otherwise can be assigned with leads.full-access.
      if (permissions['lead.all'] || permissions['lead.create'] || permissions['lead.edit']) {
        return { ...member, canBeAssigned: true };
      }

      return { ...member, canBeAssigned: false };;
    });
  }, [orgMembers]);

  const show = () => {
    toggleMemberDropdown(true);
  };

  const dismiss = () => {
    toggleMemberDropdown(false);
  };

  const handleAssignmentClick = async (e, loginId, isActive) => {
    e.stopPropagation();
    if (isActive) {
      await deleteLeadAssignment(loginId);
    }
    else {
      await createLeadAssignment(loginId);
    }
  };

  const createLeadAssignment = async (loginId) => {
    dispatch({
      type: CREATE_LEAD_ASSIGNMENT,
      payload: {
        variables: {
          input: {
            loginId,
            leadId,
          },
        },
      },
    });
  };

  const deleteLeadAssignment = async (loginId) => {
    dispatch({
      type: DELETE_LEAD_ASSIGNMENT,
      payload: {
        variables: {
          input: {
            loginId,
            leadId,
          },
        },
      },
    });
  };

  useEffect(() => {
    async function fetch() {
      const { data } = await fetchOrgMembers({ orgId });
      setOrgMembers(data.organizationMembers);
    }
    fetch();
  }, []);

  useEffect(() => {
    if (assignments && assignments.length) {
      setAssignmentState(assignments);
    }
    else {
      setAssignmentState(emptyAssignment);
    }
  }, [assignments]);

  const renderAssignmentMenu = () => {
    return (
      <>
        {assignees.map(member => {
          if (!member.canBeAssigned) return null;
          const isSelected = assignmentState.find(item => item.loginId === member.loginId);
          const profileImage = member.profileImage ?? null;
          let imagePath = null;
          if (profileImage) {
            imagePath = `//${process.env.REACT_APP_AWS_CLOUDFRONT}/${imagePickSize(
              profileImage,
              'square60',
            )}`;
          }
          const displayName = (member.firstname || member.lastname) ?
            [member.firstname, member.lastname].join(' ') :
            member.email;
          return (
            <button
              key={`lead-assignment--${member.loginId}`}
              className={clsx(
                'ap-dropdown__button',
                'ap-dropdown__button--lead-assignment',
                'button--unstyled',
                { 'selected': isSelected },
              )}
              onClick={(e) => handleAssignmentClick(e, member.loginId, isSelected)}
            >
              <div className="inline-avatar">
                <div className="inline-avatar__image">
                  <Avatar
                    photo={imagePath}
                    text={!imagePath ? member.initials : null}
                    size={30}
                    borderSize={1}
                  />
                </div>
                <div className="inline-avatar__info">
                  {displayName}
                </div>
              </div>
            </button>
          );
        })}
      </>
    );

  };

  const trigger = (
    <DropdownButton
      className="button--unstyled button--lead-assignment"
      showIcon
    >
      {assignmentState.map((assignment, i) => {
        return (
          <div key={`name--${i}`}>{assignment.name || assignment.email}</div>
        );
      })}
    </DropdownButton>
  );

  return (
    <>
      <APResponsiveDropdown
        opened={showOrgMembers}
        show={show}
        dismiss={dismiss}
        triggerButton={trigger}
        placement="bottom-end"
        menuClass="lead-data-dropdown"
      >
        {renderAssignmentMenu()}
      </APResponsiveDropdown>
      {assignmentError && (
        <ErrorModal
          visible={assignmentError}
          onClose={() => setAssignmentError(false)}
          actions={(
            <button
              onClick={() => setAssignmentError(false)}
              className="button"
            >
              Close
            </button>
          )}
        >
          <p>Lead assignment couldn't be updated. Please try again.</p>
        </ErrorModal>
      )}
    </>
  );
};
